<template>
  <div
    class="template-box"
  >
    <div class="template-inner">
      <div class="content">
        <div class="icon-container">
          <img :src="require(`@/assets/images/${actions.icon}`) || require(`@/assets/images/${actions.icon}`).default" :alt="actions.icon" class="icon" />
        </div>
        <h2>{{ actions.title }}</h2>
        <p class="action-description" v-html="actions.description" />
        <AppButton
          :title="actions.buttonText"
          primary
          @click="actions.ctaAction()"
          md
          class="w-auto"
        />
        <p v-if="actions.info" class="action-info" v-html="actions.info"></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { AppButton } from '@n11/so-ui-components';

const props = defineProps({
  index: Number,
  action: Object,
});

const emit = defineEmits(['refreshTable']);
const fileRef = ref(null);
const validationId = ref(null);
const dropZoneActive = ref(false);
const fileError = ref(null);
const uploading = ref(false);
const actions = ref(props.action);


</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .action-description :deep(u) {
    font-weight: 600;
    color: $colorM50;
    text-decoration: none;
  }

}

.template-box {
  border-radius: 12px;
  width: 100%;
  text-align: center;
  position: relative;
  transition: all 150ms ease-in-out;

  & > .template-inner {
    border-radius: 12px;
    padding: 24px;
    min-height: 380px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $colorM05;
    border: 2px solid $colorM20;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
  }

  &.upload {
    border-color: transparent;
    &:before {
      content: '';
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%235D3EBCFF' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      background-position: center center;
      border-radius: 12px;
      left: -2px;
      top: -2px;
      right: -2px;
      bottom: -2px;
      position: absolute;
    }
  }

  &.download.no-template {
    border-color: $colorD20;
  }

  &.upload.no-template {
    &::before {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23D3D6DF' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    }
  }

  &.droppable .template-inner {
    background-color: $colorM10;
  }
}

.index {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: $colorM50;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon-container {
  background-color: #e1d7ff;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.icon {
  width: 90px;
  height: 90px;
}

.w-auto {
  width: auto;
}

h2 {
  margin: 8px 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: $colorN80;
}

p {
  color: $colorN80;
  font-size: 13px;
  line-height: 1.4;
  margin: 0px 0px 16px 0px;
  max-width: 270px;
}

.action-info {
  margin-top: 12px;
  margin-bottom: 0;
  font-size: 12px;
  color: $colorN80;
  max-width: 100%;
  
  :deep(span) {
    font-weight: bolder;
  }
}

.file-info {
  background-color: #fff;
  border: 1px solid #ddd;
  padding: 10px 24px 10px 10px;
  border-radius: 8px;
  margin-bottom: 20px;
  position: relative;
  max-width: 380px;
  width: 100%;

  .file-details-container {
    display: flex;
    gap: 16px;
    position: relative;

    img {
      width: 30px;
      height: 40px;
    }

    .file-details {
      width: 100%;
      text-align: left;
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
  }
}

.file-name {
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 300px;
  line-height: 20px;
}

.file-size {
  color: $colorD30;
  font-size: 14px;
  margin-top: 4px;
}

.file-status {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 13px;
  gap: 8px;
  color: $colorS50;

  &.error {
    color: $colorE50;
  }

  img {
    width: 16px;
    height: 16px;
  }
}

.file-restrictions {
  font-size: 12px;
  margin-top: 16px;
  position: absolute;
  bottom: 10px;
}

.file-restrictions::v-deep span {
  font-weight: bolder
}

.remove-file {
  position: absolute;
  right: 8px;
  top: 0px;
  font-size: 20px;
  padding: 0px;
}

.bulk-update-template-dropdowns {
  display: flex;
  gap: 12px;
  flex-direction: column;
  margin-bottom: 12px;
  max-width: 320px;
  text-align: left;
  width: 100%;

  .template-type {
    position: relative;
    z-index: 2;
    width: 100%;

    :deep(.errorText),
    :deep(.radioButton) {
      display: none;
    }

    :deep(.radioButton-container) {
      padding: 10px 14px;
    }
  }

  .template-group {
    :deep(.errorText),
    :deep(.actions-seperator) {
      display: none;
    }
  }
}
</style>
